import React, { useState, useCallback, useRef, TouchEvent } from 'react';

import Icon from '~/src/common/components/Icon';
import { useFeatureFlag } from '~/src/common/services/FeatureFlag';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import { isNumber } from '~/src/common/utils/guards';
import { useKeyboardArrowNav } from '~/src/screens/CatalogContent/ContentScreen/ImageSlider/useKeyboardArrowNav';

import * as NewStyles from './layout';
import * as OldStyles from './layout-old';

interface Props {
  className?: string;
  offsetX?: number;
  images: { id: string; url: string; ratio?: number | null; thumb: string; large: string }[];
  alt: string;
  onArrowClick?: () => void;
  onThumbnailClick?: (eventSource: 'photo modal' | 'product page') => void;
}

const ImageSlider = ({
  className,
  images,
  alt,
  onArrowClick,
  onThumbnailClick,
  offsetX = 0,
}: Props) => {
  const isNewFocus = useFeatureFlag('configuration-page-produit') !== 'old';
  // Calcul du ratio d'affichage du slider
  const ratios = images.map(image => image.ratio).filter(isNumber);
  const ratio = ratios.length > 0 ? Math.max(...ratios) : 1;

  // Gestion du changement d'image courante
  const count = images.length;
  const needsNavigation = count > 1;
  const [current, setCurrent] = useState(0);
  const normalize = useCallback((index: number) => (index + count) % count, [count]);
  const handlePrev = useCallback(() => {
    setCurrent(prev => normalize(prev - 1));
    onArrowClick?.();
  }, [normalize, onArrowClick]);
  const handleNext = useCallback(() => {
    setCurrent(prev => normalize(prev + 1));
    onArrowClick?.();
  }, [normalize, onArrowClick]);
  const handleMoveTo = useCallback(
    (index: number) => {
      setCurrent(normalize(index));
      onThumbnailClick?.('product page');
    },
    [normalize, onThumbnailClick],
  );

  useKeyboardArrowNav(handlePrev, handleNext);

  // Gestion des touch events
  const touchPositions = useRef<{ x: number; y: number }>();
  const handleTouchStart = useCallback((event: TouchEvent) => {
    const { screenX, screenY } = event.changedTouches[0];
    touchPositions.current = { x: screenX, y: screenY };
  }, []);
  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (touchPositions.current == null) return;
      const { screenX, screenY } = event.changedTouches[0];
      const deltaX = screenX - touchPositions.current.x;
      const deltaY = screenY - touchPositions.current.y;
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
      (deltaX < 0 ? handleNext : handlePrev)();
    },
    [handleNext, handlePrev],
  );

  const { show } = useNiceModal('zoomed-in-image');

  // Oblige de destructure car knip se plaint que les exports ne sont pas utilises
  const S = isNewFocus ? { ...NewStyles } : { ...OldStyles };

  return (
    <S.MainContainer className={className}>
      <S.ThumbnailsContainer>
        {images.map((img, i) => (
          <S.ThumbnailImage
            key={img.id}
            src={img.thumb}
            alt={alt}
            onClick={() => handleMoveTo(i)}
            $isActive={i === current}
          />
        ))}
      </S.ThumbnailsContainer>

      <S.Container>
        <S.ImagesWrapper
          $translateX={current}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          $withNavigation={needsNavigation}>
          {images.map((image, index) => (
            <S.ImageWrapper key={image.id} $ratio={ratio} $index={index} $offsetX={offsetX}>
              <S.Image
                src={image.url}
                alt={index > 0 ? `${alt} - ${index + 1}` : alt}
                onClick={() => {
                  void show({
                    index,
                    title: alt,
                    images,
                    onThumbnailClick: () => {
                      onThumbnailClick?.('photo modal');
                    },
                  });
                }}
              />
              <S.EyeIconWrapper>
                <Icon name="Eye-on" size={24} color="PRIMARY" />
              </S.EyeIconWrapper>
            </S.ImageWrapper>
          ))}
        </S.ImagesWrapper>

        {needsNavigation ? (
          <>
            <S.CarouselArrow
              variant="secondary"
              icon="Chevron-left"
              $direction="left"
              iconSize={30}
              onClick={handlePrev}
            />
            <S.CarouselArrow
              variant="secondary"
              icon="Chevron-right"
              $direction="right"
              iconSize={30}
              onClick={handleNext}
            />
          </>
        ) : null}
      </S.Container>
    </S.MainContainer>
  );
};

export default ImageSlider;
